/**
 * Tournament match console — local + remote play with live scoring.
 */
import { Alpine } from '../../core/alpine-core.js';
import { preferredAppOrigin } from '../../core/preferred-app-origin.js';
import { apiFetch } from '../api.js';
import QRCode from 'qrcode';
import {
  buildSessionEventUrl,
  canUseBrowserMediaDevices,
  createMatchLiveScoringMethods,
  createMatchMediaSessionMethods,
  mediaDevicesBlockedReason,
  stopLocalMedia,
  syncMicMuted,
} from '../match-media-session.js';

function parseTournamentMatchIds() {
  const parts = (globalThis.location?.pathname || '').split('/').filter(Boolean);
  const ti = parts.indexOf('tournaments');
  const mi = parts.indexOf('matches');
  if (ti < 0 || !parts[ti + 1] || mi < 0 || !parts[mi + 1]) return { tournamentId: '', matchId: '' };
  return {
    tournamentId: decodeURIComponent(parts[ti + 1]),
    matchId: decodeURIComponent(parts[mi + 1]),
  };
}

function navTo(path) {
  const router = globalThis.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else globalThis.location.assign(path);
}

function myPlayerIdFromStore(store) {
  const me = store?.me || {};
  return String(store?.rosterSelfPlayerId || me.playerId || me._id || '').trim();
}

const mediaMethods = createMatchMediaSessionMethods({
  sessionApiBase: (ctx) => `/tournaments/${encodeURIComponent(ctx.tournamentId)}/matches/${encodeURIComponent(ctx.matchId)}`,
  isRemote: (ctx) => ctx.isRemotePlay(),
  isParticipant: (ctx) => ctx.isEntrant(),
  myPlayerId: (ctx) => ctx.myPlayerId(),
  otherPlayerId: (ctx) => ctx.otherPlayerId(),
});

const liveMethods = createMatchLiveScoringMethods({
  liveApiBase: (ctx) => `/tournaments/${encodeURIComponent(ctx.tournamentId)}/matches/${encodeURIComponent(ctx.matchId)}`,
  liveScoringFromSession: (ctx) => ctx.session?.liveScoring || {},
  myPlayerId: (ctx) => ctx.myPlayerId(),
  isLocal: (ctx) => !ctx.isRemotePlay(),
  onMatchFinished: async (ctx) => {
    await ctx.completeMatchFromLive();
  },
});

Alpine.data('tournamentMatchConsole', () => ({
  loading: true,
  busy: false,
  sessionLoading: false,
  mediaBusy: false,
  error: '',
  tournamentId: '',
  matchId: '',
  match: null,
  session: null,
  eventSource: null,
  eventStreamHealthy: false,
  micMuted: false,
  localStream: null,
  remoteStream: null,
  peer: null,
  webrtcReady: false,
  mediaStatusText: '',
  liveVisitForm: { score: '' },
  showSessionPanel: true,
  _pollTimer: null,
  tvPairCode: '',
  tvPairBusy: false,
  tvPairError: '',
  tvPairMessage: '',
  tvLinkedActive: false,
  scorerQrCanvas: null,
  _scorerQrRenderAttempts: 0,

  async init() {
    const { tournamentId, matchId } = parseTournamentMatchIds();
    this.tournamentId = tournamentId;
    this.matchId = matchId;
    if (!tournamentId || !matchId) {
      this.error = 'Invalid match URL';
      this.loading = false;
      return;
    }
    try {
      await this.loadMatch();
      if (this.match?.schedule?.status !== 'live' && this.match?.schedule?.status !== 'completed') {
        await this.ensureStarted();
      }
      await this.refreshSession();
      this.connectEventStream();
      if (this.isRemotePlay() && this.session) this.showSessionPanel = String(this.session.status) !== 'live';
    } catch (e) {
      this.error = e.message || String(e);
    } finally {
      this.loading = false;
      this.scheduleDevicesPanelPaint();
    }
  },

  /** After `loading` clears, devices markup is x-shown — defer QR paint until refs exist. */
  scheduleDevicesPanelPaint() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        void this.renderScorerQr();
        void this.refreshTvLinkStatus();
      });
    });
  },

  destroy() {
    this.disconnectEventStream();
    this.stopSessionPollFallback();
    stopLocalMedia(this);
  },

  goTo(path) {
    navTo(path);
  },

  myPlayerId() {
    return myPlayerIdFromStore(this.$store?.session);
  },

  otherPlayerId() {
    const mine = this.myPlayerId();
    for (const p of this.match?.participants || []) {
      for (const rid of p.refIds || []) {
        if (String(rid) !== mine) return String(rid);
      }
    }
    return '';
  },

  isEntrant() {
    const pid = this.myPlayerId();
    if (!pid) return false;
    return (this.match?.participants || []).some((p) => (p.refIds || []).map(String).includes(pid));
  },

  isRemotePlay() {
    return String(this.match?.playMode || this.match?.schedule?.playMode || 'local') === 'remote';
  },

  participantSide(side) {
    return (this.match?.participants || []).find((p) => p.side === side) || null;
  },

  participantA() {
    return this.participantSide('A');
  },

  participantB() {
    return this.participantSide('B');
  },

  legsWon(side) {
    const ls = liveMethods.liveScoring(this);
    if (side === 'A') return Number(ls.legsWonA) || 0;
    return Number(ls.legsWonB) || 0;
  },

  sessionStatusLabel() {
    const s = String(this.session?.status || '');
    if (s === 'live') return 'Live';
    if (s === 'completed') return 'Completed';
    if (s === 'connecting') return 'Connecting';
    if (s === 'waiting') return 'Waiting';
    return s || 'Setup';
  },

  sessionStatusClasses() {
    const s = String(this.session?.status || '');
    if (s === 'live') return 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30';
    if (s === 'completed') return 'bg-neutral-500/20 text-neutral-300 border border-neutral-400/30';
    return 'bg-amber-500/20 text-amber-200 border border-amber-400/30';
  },

  canConnectSession() {
    return this.isRemotePlay() && this.session && String(this.session.status) !== 'live';
  },

  canMarkSessionReady() {
    if (!this.isRemotePlay() || !this.session) return false;
    const mine = mediaMethods.mySessionParticipant(this);
    return !!mine?.connected;
  },

  isMatchLive() {
    return mediaMethods.isMatchLive(this);
  },

  isMyTurn() {
    return liveMethods.isMyTurn(this);
  },

  mySide() {
    return liveMethods.mySide(this);
  },

  remainingFor(side) {
    return liveMethods.remainingFor(this, side);
  },

  async loadMatch() {
    const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Match load failed');
    this.match = data.match || null;
    this.session = data.match?.session || null;
  },

  async ensureStarted() {
    const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/start`, {
      method: 'POST',
      body: { playMode: this.match?.playMode || 'local' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Could not start match');
    this.match = data.match || this.match;
    this.session = data.session || data.match?.session || this.session;
  },

  async refreshSession() {
    await mediaMethods.loadSession(this);
  },

  async connectSession() {
    await mediaMethods.connectSession(this);
  },

  async markSessionReady() {
    await mediaMethods.markSessionReady(this);
  },

  async addLiveVisit() {
    await liveMethods.addLiveVisit(this);
  },

  async undoLiveVisit() {
    await liveMethods.undoLiveVisit(this);
  },

  async startNextLeg() {
    await liveMethods.startNextLeg(this);
  },

  applySessionFromStream(payload) {
    if (!payload?.session) return;
    this.session = payload.session;
    this.eventStreamHealthy = true;
    if (this.isRemotePlay()) mediaMethods.syncPeerFromSession(this);
    if (payload.session?.liveScoring?.matchFinished) this.completeMatchFromLive();
  },

  startSessionPollFallback() {
    if (this._pollTimer) return;
    this._pollTimer = globalThis.setInterval(() => {
      if (!this.tournamentId || !this.matchId) return;
      void this.refreshSession();
    }, 2000);
  },

  stopSessionPollFallback() {
    if (!this._pollTimer) return;
    globalThis.clearInterval(this._pollTimer);
    this._pollTimer = null;
  },

  connectEventStream() {
    this.disconnectEventStream();
    const url = buildSessionEventUrl(`/api/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/session/events`);
    if (!globalThis.EventSource) {
      this.startSessionPollFallback();
      return;
    }
    try {
      const es = new EventSource(url);
      this.eventSource = es;
      es.addEventListener('session:update', (ev) => {
        try {
          this.applySessionFromStream(JSON.parse(ev.data || '{}'));
        } catch { /* ignore */ }
      });
      es.onerror = () => {
        this.eventStreamHealthy = false;
        this.startSessionPollFallback();
      };
      es.onopen = () => {
        this.eventStreamHealthy = true;
        this.stopSessionPollFallback();
      };
    } catch {
      this.startSessionPollFallback();
    }
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        if (document.visibilityState === 'visible') void this.refreshSession();
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  },

  disconnectEventStream() {
    if (this.eventSource) {
      try { this.eventSource.close(); } catch { /* ignore */ }
      this.eventSource = null;
    }
    if (this._visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  },

  toggleMic() {
    this.micMuted = !this.micMuted;
    syncMicMuted(this);
  },

  matchFinished() {
    return !!this.session?.liveScoring?.matchFinished;
  },

  matchWinnerDisplayName() {
    const ls = this.session?.liveScoring || {};
    let side = ls.winnerSide;
    if (!side) {
      const bo = Math.max(1, Number(this.match?.format?.bestOfLegs) || 3);
      const need = Math.floor(bo / 2) + 1;
      const a = this.legsWon('A');
      const b = this.legsWon('B');
      if (a >= need) side = 'A';
      else if (b >= need) side = 'B';
    }
    if (!side) return 'Winner';
    return this.participantSide(side)?.displayName || `Player ${side}`;
  },

  finalLegScoreLabel() {
    return `${this.legsWon('A')} – ${this.legsWon('B')}`;
  },

  tournamentHasBracket() {
    return !!this.match?.hasBracket;
  },

  tournamentExitPath() {
    if (this.tournamentHasBracket()) return this.bracketUrl();
    return '/admin/tournaments';
  },

  tournamentExitLabel() {
    return this.tournamentHasBracket() ? 'Go to bracket' : 'Back to tournaments';
  },

  matchPlayLocked() {
    return this.matchFinished() || this.busy;
  },

  bracketUrl() {
    return `/admin/tournaments/${encodeURIComponent(this.tournamentId)}/bracket`;
  },

  scorerUrl() {
    return this.match?.urls?.scorer || `/admin/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/scorer`;
  },

  boardUrl() {
    return this.match?.urls?.board || `/admin/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/board`;
  },

  tvPublicUrl() {
    return `${preferredAppOrigin()}/tv`;
  },

  tvPairAdminUrl() {
    const tid = encodeURIComponent(this.tournamentId);
    const mid = encodeURIComponent(this.matchId);
    return `/admin/tv-pair?tournamentId=${tid}&tournamentMatchId=${mid}`;
  },

  scorerUrlAbsolute() {
    if (!this.tournamentId || !this.matchId) return '';
    const base = preferredAppOrigin();
    const path = `/admin/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/scorer`;
    try {
      return new URL(path, base).href;
    } catch {
      return `${base}${path}`;
    }
  },

  isMobileViewport() {
    return globalThis.matchMedia?.('(max-width: 639px)')?.matches ?? globalThis.innerWidth < 640;
  },

  showDevicesRow() {
    return !!this.match && !this.error;
  },

  showScorerQrStrip() {
    return this.showDevicesRow() && !this.isMobileViewport();
  },

  canPairTv() {
    return !!this.tournamentId && !!this.matchId;
  },

  async pairTvToMatch() {
    this.tvPairError = '';
    this.tvPairMessage = '';
    const digits = String(this.tvPairCode || '').replace(/\D/g, '');
    const code = digits.length > 4 ? digits.slice(-4) : digits.padStart(4, '0');
    if (!code || code.length < 4 || !this.tournamentId || !this.matchId) return;
    this.tvPairBusy = true;
    try {
      const res = await apiFetch('/tv-sessions/pair', {
        method: 'POST',
        body: {
          pairCode: code,
          tournamentId: this.tournamentId,
          tournamentMatchId: this.matchId,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Pair failed');
      this.tvPairCode = '';
      this.tvPairMessage = 'TV paired — the display should switch to this match shortly.';
      this.tvLinkedActive = true;
      this.$store?.toastr?.success?.('TV paired', 3500);
    } catch (e) {
      this.tvPairError = e.message || 'Pair failed';
    } finally {
      this.tvPairBusy = false;
    }
  },

  async refreshTvLinkStatus() {
    if (!this.tournamentId || !this.matchId) {
      this.tvLinkedActive = false;
      return;
    }
    try {
      const res = await apiFetch(
        `/tv-sessions/tournament/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/link`,
      );
      const data = await res.json().catch(() => ({}));
      this.tvLinkedActive = !!(res.ok && data?.ok && data.linked);
    } catch {
      this.tvLinkedActive = false;
    }
  },

  async renderScorerQr() {
    if (!this.showScorerQrStrip()) return;
    const mount = this.$refs?.scorerQr;
    if (!mount) {
      if (this._scorerQrRenderAttempts < 12) {
        this._scorerQrRenderAttempts += 1;
        globalThis.requestAnimationFrame?.(() => void this.renderScorerQr());
      }
      return;
    }
    this._scorerQrRenderAttempts = 0;
    const data = this.scorerUrlAbsolute();
    if (!data) return;
    if (!this.scorerQrCanvas || !mount.contains(this.scorerQrCanvas)) {
      this.scorerQrCanvas = document.createElement('canvas');
      this.scorerQrCanvas.width = 144;
      this.scorerQrCanvas.height = 144;
      this.scorerQrCanvas.className = 'block h-36 w-36 rounded-md';
      mount.innerHTML = '';
      mount.appendChild(this.scorerQrCanvas);
    }
    try {
      await QRCode.toCanvas(this.scorerQrCanvas, data, {
        width: 144,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
    } catch (e) {
      console.error('Failed to render tournament scorer QR', e);
    }
  },

  openScorerWindow() {
    const url = this.scorerUrlAbsolute();
    if (!url) return;
    const handle = globalThis.open(
      url,
      `pdl-tournament-scorer-${this.tournamentId}-${this.matchId}`,
      'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes',
    );
    if (!handle) globalThis.location.assign(url);
  },

  openBoardWindow() {
    const url = this.boardUrl();
    const handle = globalThis.open(
      url,
      `pdl-tournament-board-${this.tournamentId}-${this.matchId}`,
      'popup=yes,width=1600,height=900,resizable=yes,scrollbars=yes',
    );
    if (!handle) globalThis.location.assign(url);
  },

  async completeMatchFromLive() {
    if (this.busy || !this.session?.liveScoring?.matchFinished) return;
    this.busy = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}/complete`, {
        method: 'POST',
        body: {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Complete failed');
      this.$store?.toastr?.success?.('Match complete — bracket updated');
      navTo(this.bracketUrl());
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Complete failed');
    } finally {
      this.busy = false;
    }
  },

  onLiveScoreKeydown(ev) {
    if (ev.key === 'Enter' && this.liveVisitForm?.score !== '') {
      ev.preventDefault();
      this.addLiveVisit();
    }
  },

  canUseBrowserMediaDevices() {
    return canUseBrowserMediaDevices();
  },

  mediaBlockedReason() {
    return mediaDevicesBlockedReason();
  },
}));
